import React, { useState } from "react";
import { Box, Collapse, IconButton, Typography, useTheme } from "@mui/material";
import { ExpandMoreRounded } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import type { ClientPortalCampaign } from "../../store/apis/clientPortalApi";
import { PlatformTag, StatusPill } from "./ClientUiViewClient";
import {
  campaignPath,
  formatCompact,
  formatNumber,
  formatDate,
  formatMoney,
  SIDEBAR_BACKGROUND,
  subcampaignPath,
} from "./clientFormatViewClient";
import {
  TITLE_SOURCE_ATTR,
  ROW_ATTR,
  useClientTransition,
} from "./clientTransitionViewClient";

// Columnas compartidas por campañas y subcampañas, para que todo quede alineado.
// Los datos son cortos y van a ancho fijo; el nombre (largo en prod) se queda
// con todo el sobrante.
const GRID = {
  display: "grid",
  gridTemplateColumns:
    "minmax(0, 1fr) 130px 110px 132px 104px 104px 110px 44px",
  alignItems: "center",
  columnGap: 2,
} as const;

// Las subcampañas tienen menos datos (sin objetivo, estado ni fechas): sus
// columnas son las de la derecha de GRID, sin huecos, y el nombre se queda el
// resto. Impresiones y clics caen bajo inicio y fin; inversión, bajo inversión.
const SUB_GRID = {
  ...GRID,
  gridTemplateColumns: "minmax(0, 1fr) 132px 104px 104px 110px 44px",
} as const;

// La campaña no lleva plataforma (cada subcampaña puede ir en una distinta):
// su nombre ocupa también esa columna.
const WIDE = { gridColumn: "span 2" } as const;

const HeaderCell: React.FC<{ children?: React.ReactNode; wide?: boolean }> = ({
  children,
  wide,
}) => (
  <Typography
    component="span"
    sx={{
      ...(wide ? WIDE : {}),
      fontSize: 11,
      fontWeight: 600,
      color: "text.secondary",
      letterSpacing: "0.04em",
      textTransform: "uppercase",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    }}
  >
    {children}
  </Typography>
);

const NameCell: React.FC<{
  children: string;
  strong?: boolean;
  wide?: boolean;
}> = ({ children, strong, wide }) => (
  <Typography
    component="span"
    title={children}
    {...{ [TITLE_SOURCE_ATTR]: "" }}
    sx={{
      ...(wide ? WIDE : {}),
      // Del ancho del texto: es lo que viaja hasta el título de la página.
      justifySelf: "start",
      maxWidth: "100%",
      fontSize: strong ? 14 : 13,
      fontWeight: strong ? 700 : 500,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    }}
  >
    {children}
  </Typography>
);

const TextCell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    component="span"
    sx={{ fontSize: 13, whiteSpace: "nowrap", overflow: "hidden" }}
  >
    {children}
  </Typography>
);

/**
 * Campañas en una sola fila cada una, con sus subcampañas desplegadas debajo
 * (también una fila cada una). Clic en la fila para abrirla.
 */
const CampaignsTableViewClient: React.FC<{
  campaigns: ClientPortalCampaign[];
}> = ({ campaigns }) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const go = useClientTransition();
  const language = i18n.language;
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const toggle = (id: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const rowHover = {
    cursor: "pointer",
    transition: "background-color 0.15s ease",
    // El mismo azul que el sidebar, para que la fila se lea como "ábreme".
    "&:hover": { backgroundColor: SIDEBAR_BACKGROUND[theme.palette.mode] },
    "&:focus-visible": {
      outline: `2px solid ${theme.palette.info.main}`,
      outlineOffset: -2,
    },
  };

  // La fila pulsada se abre hasta ser la página (ver clientTransitionViewClient).
  const openOnClick =
    (to: string) => (event: React.MouseEvent<HTMLDivElement>) =>
      go(to, { from: event.currentTarget });
  const openOnKey =
    (to: string) => (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "Enter") go(to, { from: event.currentTarget });
    };

  return (
    <Box sx={{ overflowX: "auto", mx: { xs: -2.5, md: -3.5 } }}>
      <Box sx={{ minWidth: 1080, px: { xs: 2.5, md: 3.5 } }}>
        <Box sx={{ ...GRID, px: 2.5, pb: 2 }}>
          <HeaderCell wide>{t("clientView.table.campaign")}</HeaderCell>
          <HeaderCell>{t("clientView.table.goal")}</HeaderCell>
          <HeaderCell>{t("clientView.table.status")}</HeaderCell>
          <HeaderCell>{t("clientView.table.start")}</HeaderCell>
          <HeaderCell>{t("clientView.table.end")}</HeaderCell>
          <HeaderCell>{t("clientView.table.investment")}</HeaderCell>
          <span />
        </Box>

        {campaigns.map((campaign) => {
          const open = !collapsed.has(campaign.id);
          const hasSubs = campaign.subcampaigns.length > 0;
          return (
            <Box
              key={campaign.id}
              sx={{ borderTop: `1px solid ${theme.palette.divider}` }}
            >
              <Box
                role="link"
                {...{ [ROW_ATTR]: campaign.id }}
                tabIndex={0}
                onClick={openOnClick(campaignPath(campaign.id))}
                onKeyDown={openOnKey(campaignPath(campaign.id))}
                sx={{
                  ...GRID,
                  ...rowHover,
                  px: 2.5,
                  py: 1.75,
                  borderRadius: "16px",
                  my: 0.5,
                }}
              >
                <NameCell strong wide>
                  {campaign.name}
                </NameCell>
                <TextCell>{t(`clientView.kpi.${campaign.kpi}`)}</TextCell>
                <Box>
                  <StatusPill status={campaign.status} />
                </Box>
                <TextCell>{formatDate(campaign.startDate, language)}</TextCell>
                <TextCell>{formatDate(campaign.endDate, language)}</TextCell>
                <TextCell>
                  {formatMoney(
                    campaign.subcampaigns.reduce(
                      (sum, s) => sum + s.investment,
                      0
                    ),
                    campaign.currency,
                    language
                  )}
                </TextCell>
                {hasSubs ? (
                  <IconButton
                    size="small"
                    aria-label={
                      open
                        ? t("clientView.table.collapse")
                        : t("clientView.table.expand")
                    }
                    aria-expanded={open}
                    onClick={(event) => {
                      event.stopPropagation();
                      toggle(campaign.id);
                    }}
                  >
                    <ExpandMoreRounded
                      sx={{
                        transition: "transform 0.2s ease",
                        transform: open ? "rotate(180deg)" : "none",
                      }}
                    />
                  </IconButton>
                ) : (
                  <span />
                )}
              </Box>

              {hasSubs && (
                <Collapse in={open} timeout="auto">
                  <Box sx={{ pl: 4, pb: 2.5 }}>
                    <Box
                      sx={{
                        borderLeft: `2px solid ${theme.palette.divider}`,
                        pl: 1,
                      }}
                    >
                      <Box sx={{ ...SUB_GRID, px: 2.5, pt: 0.5, pb: 1 }}>
                        <HeaderCell>
                          {t("clientView.table.subcampaign")}
                        </HeaderCell>
                        <HeaderCell>
                          {t("clientView.table.platform")}
                        </HeaderCell>
                        <HeaderCell>
                          {t("clientView.table.impressions")}
                        </HeaderCell>
                        <HeaderCell>{t("clientView.table.clicks")}</HeaderCell>
                        <HeaderCell>
                          {t("clientView.table.investment")}
                        </HeaderCell>
                        <span />
                      </Box>
                      {campaign.subcampaigns.map((sub) => {
                        const to = subcampaignPath(campaign.id, sub.id);
                        return (
                          <Box
                            key={sub.id}
                            role="link"
                            {...{ [ROW_ATTR]: sub.id }}
                            tabIndex={0}
                            onClick={openOnClick(to)}
                            onKeyDown={openOnKey(to)}
                            sx={{
                              ...SUB_GRID,
                              ...rowHover,
                              px: 2.5,
                              py: 1.25,
                              borderRadius: "14px",
                            }}
                          >
                            <NameCell>{sub.name}</NameCell>
                            <PlatformTag platform={sub.platform} />
                            <TextCell>
                              {formatNumber(sub.impressions, language)}
                            </TextCell>
                            <TextCell>
                              {formatCompact(sub.clicks, language)}
                            </TextCell>
                            <TextCell>
                              {formatMoney(
                                sub.investment,
                                campaign.currency,
                                language
                              )}
                            </TextCell>
                            <span />
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                </Collapse>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default CampaignsTableViewClient;
